/* eslint-disable react-hooks/rules-of-hooks */
import { useEffect, useState } from "react";
import { useAuth } from "@frontegg/react";
import MainHeader from "./header";
import MainDrawer from "./drawer";
import WatermarkOverlay from "../../components/common/watermark-overlay/WatermarkOverlay";
import { usePageLogger } from "../../hooks/usePageLogger";
import { useContentProtection } from "../../hooks/useContentProtection";
import { usePreviewSession } from "../../contexts/PreviewSessionContext";

const DEV_AUTH_BYPASS = import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH_BYPASS === "true";

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [clientIp, setClientIp] = useState<string>("");
  const previewCtx = usePreviewSession();
  // Frontegg's useAuth requires its provider; in preview mode there is no
  // FronteggProvider, so call it conditionally. The branch is stable for the
  // lifetime of the App instance (decided in main.tsx), satisfying React's
  // rules-of-hooks invariant.
  const fronteggUser = previewCtx ? null : useAuth().user;
  const previewEmail = previewCtx?.session?.attributedEmail ?? "";
  const email = previewEmail
    ? previewEmail
    : DEV_AUTH_BYPASS
    ? "dev@fixfinder.com"
    : fronteggUser?.email ?? "";

  usePageLogger({ userEmail: email || null, userType: "authenticated" });
  useContentProtection();

  useEffect(() => {
    fetch("/api/user/ip")
      .then((r) => r.json())
      .then((d) => { if (d.ip) setClientIp(d.ip); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1280) {
        setIsOpen(false);
      } else {
        setIsOpen(true);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="flex h-screen bg-gray-100">
      {email && <WatermarkOverlay email={email} ip={clientIp} />}
      <MainDrawer isOpen={isOpen} setIsOpen={setIsOpen} />
      <div className="flex-1 flex flex-col transition-all duration-300">
        <MainHeader isOpen={isOpen} setIsOpen={setIsOpen} />
        <div
          id="main-content"
          className={`flex-1 overflow-auto outline-none p-6 pr-[4rem] pt-[90px] transition-all duration-300 bg-[#05050f] ${
            isOpen ? "pl-[20rem]" : "pl-[9rem] pr-[2.2rem]"
          }`}
          style={{
            WebkitOverflowScrolling: 'touch',
            overscrollBehaviorY: 'contain'
          }}
        >
          {children}
        </div>
      </div>
      <a
        href="/api/honeypot"
        aria-hidden="true"
        tabIndex={-1}
        style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }}
        onClick={(e) => {
          e.preventDefault();
          import("@fingerprintjs/fingerprintjs").then((FP) =>
            FP.load().then((agent) => agent.get())
          ).then((result) => {
            fetch("/api/honeypot", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ email, fingerprint: result.visitorId }),
            }).catch(() => {});
          }).catch(() => {
            fetch("/api/honeypot", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ email }),
            }).catch(() => {});
          });
        }}
      >
        admin-access
      </a>
    </div>
  );
}
